/*
 * Panel (alias Card): the white 20 px surface every page is built from, on the tinted app canvas.
 *   <Panel>
 *     <PanelHeader title={…} description={…} actions={<Button size="sm">…</Button>} />
 *     <PanelBody>…</PanelBody>
 *     <PanelFooter>…</PanelFooter>
 *   </Panel>
 * `padded` puts the content straight in with 20/24 px padding (no header/body parts needed).
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

type PanelProps = HTMLAttributes<HTMLDivElement> & {
  padded?: boolean
  /** interactive card: hover lift and ring (wrap in a Link or give it an onClick) */
  interactive?: boolean
  as?: 'div' | 'section' | 'article' | 'aside'
  tone?: 'default' | 'quiet' | 'brand'
}

export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  { padded, interactive, as: As = 'div', tone = 'default', className, ...rest },
  ref,
) {
  return (
    <As
      ref={ref}
      className={cn(
        'rounded-panel ring-1 ring-card-line',
        tone === 'default' && 'bg-surface shadow-panel',
        tone === 'quiet' && 'bg-surface-soft',
        tone === 'brand' && 'bg-brand-50/60 ring-brand-100',
        padded && 'p-5 sm:p-6',
        interactive &&
          'transition-[box-shadow,transform] duration-200 focus-within:ring-card-line-hover hover:shadow-card-hover hover:ring-card-line-hover',
        className,
      )}
      {...rest}
    />
  )
})

export const Card = Panel

export function PanelHeader({
  title,
  description,
  actions,
  icon,
  className,
  titleAs: H = 'h2',
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  icon?: ReactNode
  className?: string
  titleAs?: 'h2' | 'h3'
}) {
  return (
    <div className={cn('flex flex-wrap items-start gap-x-4 gap-y-3 px-5 pt-5 sm:px-6 sm:pt-6', className)}>
      {icon ? (
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-surface-tint text-brand-600 ring-1 ring-hairline [&_svg]:size-[18px]">
          {icon}
        </span>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <H className="text-16 font-semibold text-ink">{title}</H>
        {description ? <p className="text-13 text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export function PanelBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-5 sm:px-6', className)} {...rest} />
}

export function PanelFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-2 rounded-b-panel border-t border-hairline bg-surface-soft px-5 py-3.5 sm:px-6',
        className,
      )}
      {...rest}
    />
  )
}
